import FileSaver from "file-saver";
import {BudgetingType} from "../../../BudgetingType";
import {AppConfig} from "../../../hooks/useConfig";
import {ExportPropertyBudgetQuery, GetBudgetSeasonExportDataQuery, VersionType} from "../../../__generated__/generated_types";
import {YearDefinition} from "../../annualsummary/YearDefinitions";
import {BudgetExportFormat} from "../BudgetExportData";
import {
    formatForExcelFullNoZeros,
    formatForExcelFullNoZerosWithMetrics,
    formatForExcelFullNoZerosWithThisYearBudget,
    formatForExcelThreeWayComparison,
    formatForExcelWithoutRawAccounts,
    formatForArbitraryMonthRange
} from "./formatters";
import {getMonthIndexes} from "../../../utils/months-helper";
import {Property} from "../../../contexts/properties/logic";
import {formatExportTimestampSlug} from "../../../utils/formatters";

export function exportExcel(
    exportFormat: BudgetExportFormat.EXCEL_FULL_NO_ZEROS
        | BudgetExportFormat.EXCEL_WITHOUT_RAW_ACCOUNTS_NO_ZEROS
        | BudgetExportFormat.EXCEL_FULL_NO_ZEROS_WITH_METRICS
        | BudgetExportFormat.EXCEL_FULL_NO_ZEROS_WITH_THIS_YEAR_BUDGET
        | BudgetExportFormat.EXCEL_THREE_WAY_COMPARISON,
    tab: BudgetingType,
    referenceYear: YearDefinition,
    extraReferenceYear: {
        referenceYear: number,
        referenceValueType: VersionType
    },
    data: ExportPropertyBudgetQuery,
    config: AppConfig,
    unitsCount: number | undefined,
    unitsTotalSQFT: number | undefined
): void {
    let formattedDataPromise;
    if (exportFormat == BudgetExportFormat.EXCEL_FULL_NO_ZEROS) {
        formattedDataPromise = formatForExcelFullNoZeros({
            tab: tab,
            referenceYear: referenceYear,
            data: data,
            config: config
        });
    }
    else if (exportFormat == BudgetExportFormat.EXCEL_FULL_NO_ZEROS_WITH_THIS_YEAR_BUDGET) {
        formattedDataPromise = formatForExcelFullNoZerosWithThisYearBudget({
            tab: tab,
            referenceYear: referenceYear,
            extraReferenceYear,
            data: data,
            config: config
        });
    }
    else if (exportFormat == BudgetExportFormat.EXCEL_THREE_WAY_COMPARISON) {
        formattedDataPromise = formatForExcelThreeWayComparison({
            tab: tab,
            referenceYear: referenceYear,
            extraReferenceYear,
            data: data,
            config: config
        });
    }
    else if (exportFormat == BudgetExportFormat.EXCEL_WITHOUT_RAW_ACCOUNTS_NO_ZEROS) {
        formattedDataPromise = formatForExcelWithoutRawAccounts({
            tab: tab,
            compareToYear: referenceYear,
            referenceYear: extraReferenceYear,
            data: data,
            config: config
        });
    }
    else if (exportFormat == BudgetExportFormat.EXCEL_FULL_NO_ZEROS_WITH_METRICS) {
        if(unitsCount !== undefined && unitsTotalSQFT !== undefined) {
            const property = config.properties.currentProperty;
            if(property && property.isAcquisitionProperty) {
                formattedDataPromise = formatForExcelFullNoZerosWithMetrics({
                    tab: BudgetingType.BUDGET,
                    referenceYear: {
                        year: property.reforecastYear,
                        type: VersionType.YearTotal
                    },
                    customReportYear: property.acquisitionBudgetYear,
                    customMonthIndexes: getMonthIndexes(property),
                    referenceYearCustomLabel: "T12",
                    data: data,
                    config: {
                        year: property.reforecastYear,
                        properties: {
                            currentProperty: property
                        },
                        chartOfAccountsConfig: {
                            getIsIncome: id => config.chartOfAccountsConfig.getIsIncome(id)
                        }
                    },
                    unitsCount,
                    unitsTotalSQFT
                });
            } else {
                formattedDataPromise = formatForExcelFullNoZerosWithMetrics({
                    tab: tab,
                    referenceYear: referenceYear,
                    data: data,
                    config: config,
                    unitsCount,
                    unitsTotalSQFT
                });
            }
        }
    }

    if (formattedDataPromise) {
        formattedDataPromise.then(saveData => {
            let year = config.year;
            let periodName = "Reforecast";
            if (tab == BudgetingType.BUDGET) {
                year = config.year + 1;
                periodName = "Budget";
            }

            const property = config.properties.currentProperty;
            if(property && property.isAcquisitionProperty) {
                year = property.acquisitionBudgetYear ?? property.reforecastYear;
                periodName = "Acquisition Budget";
            }
            const fileName = `${year} ${periodName} - ${config.properties.currentProperty?.name ?? ""}`;
            FileSaver.saveAs(saveData, `${fileName}_${formatExportTimestampSlug(new Date())}.xlsx`);
        });
    }
}

export function exportArbitraryMonthRangeExcel(params:{
    data: GetBudgetSeasonExportDataQuery,
    startMonth: number, // from 0 to 23
    months: number, // from 1 to 24 minus start month is inclusive
    property: Property,
    budgetYear: number
}): void {
    const {data, startMonth, months, property, budgetYear} = params;
    const formattedDataPromise = formatForArbitraryMonthRange(data, startMonth, months, property, budgetYear, true);
    if (formattedDataPromise) {
        formattedDataPromise.then(saveData => {
            const fileName = `Forward 12 Month Forecast - ${property.name}`;
            FileSaver.saveAs(saveData, `${fileName}_${formatExportTimestampSlug(new Date())}.xlsx`);
        });
    }
}